import { FirebaseService } from '../services/FirebaseService';
import { Timestamp } from 'firebase/firestore';
import { Bundle } from '../models/interfaces';

export interface CreateBundleData extends Omit<Bundle, 'id' | 'createdAt' | 'updatedAt'> {}
export interface UpdateBundleData extends Partial<Omit<Bundle, 'id' | 'createdAt'>> {}

export class BundleService extends FirebaseService {
  constructor(private readonly foodTruckId: string) {
    super(`foodTrucks/${foodTruckId}/bundles`);
  }

  async getBundles(): Promise<Bundle[]> {
    try {
      const bundles = await this.getAll({ orderBy: 'createdAt' });
      return bundles as Bundle[];
    } catch (error) {
      console.error("Error fetching bundles:", error);
      throw error;
    }
  }

  async updateBundle(bundle: CreateBundleData | (UpdateBundleData & { id: string })): Promise<Bundle> {
    try {
      const bundleId = 'id' in bundle ? bundle.id : undefined;
      const cleanedData = this.cleanBundleData(bundle);

      if (!bundleId) {
        // Nouvelle formule
        const docRef = await this.add({
          ...cleanedData,
          createdAt: Timestamp.now()
        });
        return { ...bundle, id: docRef.id } as Bundle;
      } else {
        // Mise à jour d'un bundle existant
        await this.update(bundleId, {
          ...cleanedData,
          updatedAt: Timestamp.now()
        });
        return bundle as Bundle;
      }
    } catch (error) {
      console.error("Error updating bundle:", error);
      throw error;
    }
  }

  async deleteBundle(bundleId: string): Promise<void> {
    try {
      await this.delete(bundleId);
    } catch (error) {
      console.error("Error deleting bundle:", error);
      throw error;
    }
  }

  private cleanBundleData<T extends object>(bundle: T): Partial<T> {
    const cleanedBundle = { ...bundle };
    if ('id' in cleanedBundle) {
      delete (cleanedBundle as any).id;
    }

    Object.keys(cleanedBundle).forEach(key => {
      if (cleanedBundle[key as keyof typeof cleanedBundle] === undefined) {
        delete cleanedBundle[key as keyof typeof cleanedBundle];
      }
    });

    return cleanedBundle;
  }

  isAvailable(bundle: Bundle): boolean {
    if (!bundle.available) return false;

    const now = new Date();
    const currentDay = now.getDay() === 0 ? 7 : now.getDay();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    if (!bundle.validityPeriod.days.includes(currentDay)) return false;

    const restrictions = bundle.validityPeriod.restrictions?.[currentDay];
    if (restrictions?.enabled) {
      return currentTime >= restrictions.start && currentTime <= restrictions.end;
    }

    return currentTime >= bundle.validityPeriod.start &&
           currentTime <= bundle.validityPeriod.end;
  }
}

// Export singleton instance factory
export const bundlesService = {
  getInstance(foodTruckId: string): BundleService {
    if (!foodTruckId) {
      throw new Error('Food truck ID is required');
    }
    return new BundleService(foodTruckId);
  }
};